import CloseCircleLargeIcon from '@assets/icons/close-circle-x-large.svg';
import React, { useRef, useState } from 'react';
import TakePhotoCircleIcon from '@assets/icons/take-photo-circle.svg';
import { CAMERA_FILTER, CAMERA_POSITION } from '@/utils/camera';
import { createStyles } from '@/contexts/theme-context';
import { TouchableOpacity, View } from 'react-native';
import {
  useCameraDevice,
  Camera as VisionCamera,
} from '@/react-native-vision-camera';
import { useCameraReady } from '@/hooks/use-camera-ready';

interface Props {
  addPhotos: (photos: string[]) => void;
  handleClose: () => void;
}

export const CreditRequestCamera: React.FC<Props> = ({
  addPhotos,
  handleClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [styles, theme] = useStyles();
  const device = useCameraDevice(CAMERA_POSITION, CAMERA_FILTER);
  const cameraRef = useRef<VisionCamera>(null);
  const { cameraReady, setCameraReady } = useCameraReady();

  const takePhoto = async () => {
    if (!cameraReady || !cameraRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      // Vision Camera returns a PhotoFile with a platform file path.
      const photo = await cameraRef.current.takePhoto();
      // Normalize to file:// URI for RN Image and downstream consumers.
      const normalizedUri = photo?.path
        ? photo.path.startsWith('file://')
          ? photo.path
          : `file://${photo.path}`
        : undefined;

      if (normalizedUri) {
        addPhotos([normalizedUri]);
      }
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        onPress={handleClose}
        style={styles.closeButton}
        testID="close-camera-button"
      >
        <CloseCircleLargeIcon fill={theme.colors.white} />
      </TouchableOpacity>
      {device ? (
        <VisionCamera
          device={device}
          enableZoomGesture
          isActive={!isLoading}
          onError={() => {
            setCameraReady(false);
          }}
          onInitialized={() => setCameraReady(true)}
          photo
          ref={cameraRef}
          style={styles.camera}
          testID="credit-request-camera"
        />
      ) : (
        // Placeholder View if camera device is not supported
        <View style={styles.camera} />
      )}
      <View style={styles.actionItemsContainer}>
        <TouchableOpacity
          disabled={isLoading}
          onPress={takePhoto}
          style={styles.cameraButton}
          testID="camera-button"
        >
          <TakePhotoCircleIcon />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const useStyles = createStyles((_theme) => ({
  actionItemsContainer: {
    bottom: 32,
    flexDirection: 'row',
    justifyContent: 'center',
    left: 0,
    paddingHorizontal: 16,
    position: 'absolute',
    right: 0,
  },
  camera: {
    flex: 1,
  },
  cameraButton: {
    height: 70,
    width: 70,
  },
  closeButton: {
    left: 16,
    position: 'absolute',
    top: 16,
    zIndex: 1,
  },
  container: {
    flex: 1,
  },
}));
